import * as React from "react"
import BuildTypeLabel from "./BuildTypeLabel"
import { ThemeCss } from "gatsby-interface"
import { BuildType, BuildRunnerType, BuildCommit } from "@modules/graphql/types"
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@reach/disclosure"
import { MdAdd, MdRemove } from "react-icons/md"

export type BuildEventTextProps = {
  buildType: BuildType
  runnerType?: BuildRunnerType
  buildSource?: string
  commit?: BuildCommit
  collapsible?: boolean
  truncate?: boolean
}

export function BuildEventText({
  buildType,
  runnerType,
  commit,
  buildSource,
  collapsible = false,
  truncate = true,
}: BuildEventTextProps) {
  const showCommit = buildType === BuildType.SourceUpdate && commit
  const commitMessage = commit?.message

  if (showCommit && commitMessage) {
    return truncate ? (
      <CommitTextDisclosure
        commitMessage={commitMessage}
        expandable={collapsible}
      />
    ) : (
      <span>{commitMessage}</span>
    )
  }

  return (
    <BuildTypeLabel
      buildType={buildType}
      buildSource={buildSource}
      runnerType={runnerType}
    />
  )
}

type CommitTextDisclosureProps = {
  commitMessage: string
  expandable?: boolean
}

function CommitTextDisclosure({
  commitMessage,
  expandable,
}: CommitTextDisclosureProps) {
  const [isOpen, setOpen] = React.useState(false)
  const COMMIT_TEXT_MAX_LENGTH = 100

  if (commitMessage.length < 80) {
    return <span>{commitMessage}</span>
  }

  // Commit title is separated from the body by at least one new line
  // which means we can get it by splitting the commit message by "\n"
  // and picking the first item from the resulting array
  const [commitTitle] = commitMessage.split(/\n/)
  // Next we can extract the "visible" part of the commit no longer than COMMIT_TEXT_MAX_LENGTH characters
  const truncatedCommitTitle = commitTitle.substr(0, COMMIT_TEXT_MAX_LENGTH)
  // and get the cutoff part to be displayed when user clicks the button to see full commit message
  const commitTitleRest = commitTitle.substr(truncatedCommitTitle.length)

  // Everything in the commit message that goes after the title is its body,
  // We also trim the body since we are going to handle spacing ourselves
  const commitBody = commitMessage
    .substr(commitTitle.length || 0)
    .trim()
    .replace(/^\s*$(?:\r\n?|\n)/gm, "")

  return (
    <Disclosure open={isOpen} onChange={() => setOpen(!isOpen)}>
      {truncatedCommitTitle}
      {commitTitleRest && <React.Fragment>&hellip;</React.Fragment>}
      {expandable && (
        <React.Fragment>
          <DisclosureButton
            aria-label="Show full commit message"
            css={disclosureButtonCss}
          >
            {/* <MdMoreHoriz css={{ verticalAlign: `middle` }} /> */}
            {isOpen ? <MdRemove /> : <MdAdd />}
          </DisclosureButton>
          <DisclosurePanel css={disclosurePanelCss}>
            {commitTitleRest && (
              <React.Fragment>
                &hellip;{commitTitleRest}
                <br />
              </React.Fragment>
            )}
            &nbsp;{commitBody}
          </DisclosurePanel>
        </React.Fragment>
      )}
    </Disclosure>
  )
}

/* styles */

const disclosureButtonCss: ThemeCss = theme => ({
  border: `1px solid ${theme.colors.purple[30]}`,
  background: theme.colors.white,
  color: theme.colors.purple[60],
  padding: `${theme.space[1]} ${theme.space[2]}`,
  marginLeft: theme.space[3],
  cursor: `pointer`,
  display: `inline-flex`,
  alignItems: `center`,
  verticalAlign: `text-bottom`,
})

const disclosurePanelCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[1],
  whiteSpace: `pre-wrap`,
  paddingBottom: theme.space[3],
  color: theme.colors.grey[60],
})
